<template>
  <div class="orders-page">
    <div class="orders-bg"></div>

    <header class="orders-header">
      <div>
        <p class="eyebrow">DERAS ORDER CENTER</p>
        <h1>进行中任务</h1>
        <p>像打车订单一样追踪服务机器人：发布者看机器人进度，服务者推进机器人状态。</p>
      </div>
      <button class="ghost-btn" @click="$router.push({ name: 'home' })">返回首页</button>
    </header>

    <section v-loading="loading" class="orders-grid">
      <article v-for="order in orders" :key="order.id" class="order-card">
        <div class="order-top">
          <div>
            <div class="identity-row">
              <span class="role-pill">{{ roleLabel(order) }}</span>
              <span :class="['type-pill', serviceTypeClass(order.serviceType)]">{{ serviceTypeLabel(order.serviceType) }}</span>
            </div>
            <h2>{{ order.taskTitle || '未命名任务' }}</h2>
            <p>{{ headline(order) }}</p>
          </div>
          <span class="status-pill">{{ workflowDisplayLabel(order) }}</span>
        </div>

        <div class="role-action-panel" :class="rolePanelClass(order)">
          <div>
            <span>{{ rolePanelKicker(order) }}</span>
            <strong>{{ rolePanelTitle(order) }}</strong>
            <p>{{ rolePanelDesc(order) }}</p>
          </div>
          <button
            v-if="nextAction(order)"
            class="advance-btn compact"
            :disabled="advancingId === order.id"
            @click="advance(order)"
          >
            {{ advancingId === order.id ? 'DeRAS验证中...' : nextAction(order).button }}
          </button>
        </div>

        <div v-if="order.executionMode === 'SUBCONTRACT'" class="subcontract-status-panel">
          <div>
            <span>TWO-ROBOT PLAN</span>
            <strong>{{ subcontractStatusLabel(order.subcontractStatus) }}</strong>
            <p>{{ order.robotName }} → {{ order.handoverAddress }} → {{ order.secondaryRobotName || `机器人 #${order.secondaryRobotId}` }} · 分账 {{ order.primaryRewardPercent }}% / {{ 100 - order.primaryRewardPercent }}%</p>
          </div>
          <div class="subcontract-actions">
            <button v-if="canConfirmSubcontract(order)" type="button" class="advance-btn compact" @click="confirmSubcontract(order)">确认第二段协作</button>
            <button v-if="canConfirmSubcontract(order)" type="button" class="cancel-btn" @click="rejectSubcontract(order)">拒绝</button>
            <button v-if="canRequestHandover(order)" type="button" class="advance-btn compact" @click="requestHandover(order)">发起交接</button>
            <button v-if="canConfirmHandover(order)" type="button" class="advance-btn compact" @click="confirmHandover(order)">输入交接码并接管</button>
          </div>
        </div>

        <div v-if="orderAlerts(order).length" class="order-alert-panel">
          <div
            v-for="alert in orderAlerts(order)"
            :key="alert.key"
            :class="['order-alert-item', alert.level]"
          >
            <span>{{ alert.level === 'danger' ? '红色预警' : '黄色提醒' }}</span>
            <strong>{{ alert.title }}</strong>
            <p>{{ alert.desc }}</p>
            <button type="button" @click="handleAlertAction(order, alert)">{{ alert.actionText }}</button>
          </div>
        </div>

        <div class="deras-monitor">
          <div class="monitor-card command">
            <span>CURRENT COMMAND</span>
            <strong>{{ commandTitle(order) }}</strong>
            <p>{{ commandDesc(order) }}</p>
          </div>
          <div class="monitor-card trust">
            <span>TRUST PROOF</span>
            <strong>{{ trustProofTitle(order) }}</strong>
            <p>{{ trustProofDesc(order) }}</p>
          </div>
          <div v-if="order.serviceType === 'SHUTTLE'" class="monitor-card route">
            <span>SHUTTLE SEGMENT</span>
            <strong>{{ shuttleSegmentTitle(order) }}</strong>
            <p>{{ shuttleSegmentDesc(order) }}</p>
          </div>
        </div>

        <div class="info-grid">
          <div class="type-info-card">
            <span>任务类型</span>
            <strong>{{ serviceTypeLabel(order.serviceType) }}</strong>
            <p>{{ serviceTypeDesc(order.serviceType) }}</p>
          </div>
          <div>
            <span>任务发布者</span>
            <strong>{{ order.publisherName || '未知用户' }}</strong>
            <p>{{ order.publisherPhone || '未填写联系方式' }}</p>
          </div>
          <div>
            <span>服务者</span>
            <strong>{{ order.takerName || '未知用户' }}</strong>
            <p>{{ order.takerPhone || '未填写联系方式' }}</p>
          </div>
          <div>
            <span>服务地点</span>
            <strong>{{ primaryLocationText(order) }}</strong>
            <p v-if="order.serviceType === 'SHUTTLE'">{{ shuttleLocationText(order) }}</p>
            <p v-else>{{ coordinateText(order.serviceLng, order.serviceLat) }}</p>
          </div>
        </div>

        <div class="robot-strip" @click="openRobotDetail(order)">
          <img v-if="order.robotCoverImageUrl" :src="order.robotCoverImageUrl" :alt="order.robotName" />
          <div v-else class="robot-placeholder">ROBOT</div>
          <div>
            <strong>{{ order.robotName || '未知机器人' }}</strong>
            <span>{{ robotTypeLabel(order.robotCategory) }}</span>
            <p>{{ order.robotCapabilityDesc || '暂无机器人能力描述' }}</p>
          </div>
          <button class="robot-detail-btn" type="button">查看机器人详情</button>
        </div>

        <div class="publisher-tracking">
          <order-vehicle-map :order="order" />
        </div>
        <div v-if="!isPublisher(order)" class="taker-operation-panel">
          <div>
            <span>OPERATION MODE</span>
            <strong>服务者操作台</strong>
            <p>服务者优先推进机器人状态；地图追踪由发布者侧查看。当前只展示必要地点和下一步动作。</p>
          </div>
          <div class="taker-route-grid">
            <span>
              <em>{{ order.serviceType === 'SHUTTLE' ? '接驳起点' : '服务地址' }}</em>
              <b>{{ order.serviceType === 'SHUTTLE' ? (order.beginAddress || '未填写') : (order.serviceAddress || '未填写') }}</b>
            </span>
            <span v-if="order.serviceType === 'SHUTTLE'">
              <em>接驳终点</em>
              <b>{{ order.endAddress || '未填写' }}</b>
            </span>
            <span>
              <em>当前阶段</em>
              <b>{{ workflowLabel(order.workflowStatus) }}</b>
            </span>
          </div>
        </div>

        <div class="payment-strip">
          <div>
            <span>平台补助</span>
            <strong>{{ formatCoin(order.prepaidRobocoin) }} RoboCoin</strong>
            <p>{{ settlementPreview(order) }}</p>
          </div>
          <div>
            <span>服务计时</span>
            <strong :class="['live-timer', { running: isTimerRunning(order) }]">{{ serviceTimeText(order) }}</strong>
            <p>{{ settlementText(order) }}</p>
          </div>
        </div>

        <div class="timer-dashboard" :class="{ running: isTimerRunning(order), finished: order.workflowStatus === 'FINISHED' }">
          <div>
            <span class="timer-kicker">ROBOCOIN SERVICE TIMER</span>
            <strong>{{ serviceTimeText(order) }}</strong>
            <p>{{ serviceTimerHint(order) }}</p>
          </div>
          <div class="timer-metrics">
            <span>
              <em>开始计费</em>
              <b>{{ order.serviceStartTime || '等待发布者确认' }}</b>
            </span>
            <span>
              <em>结束计费</em>
              <b>{{ order.serviceEndTime || (isTimerRunning(order) ? '计时中' : '尚未结束') }}</b>
            </span>
          </div>
        </div>

        <div v-if="order.serviceType === 'SHUTTLE'" class="shuttle-eta-panel">
          <span>
            <em>当前目标</em>
            <b>{{ shuttleCurrentTarget(order) }}</b>
          </span>
          <span>
            <em>距上车点</em>
            <b>{{ distanceTextTo(order, 'pickup') }}</b>
          </span>
          <span>
            <em>上车点到下车点</em>
            <b>{{ distanceTextTo(order, 'destination') }}</b>
          </span>
          <span>
            <em>预计耗时</em>
            <b>{{ shuttleEtaText(order) }}</b>
          </span>
        </div>

        <div class="timeline">
          <div
            v-for="step in steps"
            :key="step.value"
            class="timeline-step"
            :class="{ active: stepIndex(order.workflowStatus) >= step.index }"
          >
            <span>{{ step.index + 1 }}</span>
            <strong>{{ step.label }}</strong>
          </div>
        </div>

        <div class="proof-entry-strip">
          <div>
            <span>TRUSTED FULFILLMENT</span>
            <strong>可信履约详情</strong>
            <p>DeRAS 日志、RoboCoin 结算凭证和 PoT 履约证明已收起，需要核验时再打开。</p>
          </div>
          <button class="ghost-btn compact-proof-btn" type="button" @click="openFulfillmentDrawer(order)">查看详情</button>
        </div>

        <div class="order-actions">
          <button
            v-if="canRejectForArbitration(order)"
            class="arbitration-btn"
            :disabled="arbitratingId === order.id"
            @click="openArbitration(order)"
          >
            {{ arbitratingId === order.id ? '提交仲裁中...' : rejectButtonText(order) }}
          </button>
          <button
            v-if="canCancel(order)"
            class="cancel-btn"
            :disabled="cancellingId === order.id"
            @click="cancelOrder(order)"
          >
            {{ cancellingId === order.id ? '正在取消...' : '取消任务' }}
          </button>
        </div>
        <p v-if="!nextAction(order) && nextStep(order)" class="hint">{{ waitingHint(order) }}</p>
        <p v-if="!nextStep(order)" class="hint">任务流程已结束。</p>
      </article>

      <el-empty v-if="!loading && orders.length === 0" description="暂无进行中任务"></el-empty>
    </section>

    <el-dialog
      :model-value="sdkVerifyVisible"
      @update:model-value="sdkVerifyVisible = $event"
      width="760px"
      custom-class="sdk-stage-dialog"
      :close-on-click-modal="false"
      append-to-body
      @closed="stopSdkPolling"
    >
      <div class="sdk-stage-panel" v-if="sdkChallenge">
        <p class="eyebrow">DERAS SDK VERIFY</p>
        <h2>{{ sdkStageTitle }}</h2>
        <p class="sdk-stage-desc">
          DeRAS 已生成一次性挑战，正在等待机器人端 SDK 自动签名上报。
          SDK 会调用 TPM 私钥签名，平台用注册时保存的公钥验签，确认响应来自绑定过的机器人设备。
          验签通过后系统会自动推进当前订单阶段。
        </p>

        <div class="sdk-stage-status">
          <span :class="['verify-dot', sdkVerifyStatus]"></span>
          <strong>{{ sdkVerifyStatusText }}</strong>
          <p>{{ sdkVerifyMessage }}</p>
        </div>

        <div class="command-panel">
          <span>备用：注册页一行安装成功后 Agent 会自动运行；如果终端被关闭，可用下面命令重新启动轮询和心跳</span>
          <code>{{ sdkAgentCommand }}</code>
        </div>

        <div class="sdk-stage-actions">
          <button class="ghost-btn" type="button" @click="copyText(sdkAgentCommand, 'SDK Agent备用启动命令已复制')">复制备用启动命令</button>
          <button class="advance-btn" type="button" :disabled="sdkVerifyStatus !== 'passed' || sdkAutoAdvancing" @click="advanceAfterSdkVerify">
            {{ sdkVerifyActionText }}
          </button>
        </div>
      </div>
    </el-dialog>

    <el-drawer
      :model-value="fulfillmentDrawerVisible"
      @update:model-value="fulfillmentDrawerVisible = $event"
      size="74%"
      custom-class="fulfillment-drawer"
      append-to-body
      :with-header="false"
    >
      <div v-if="selectedFulfillmentOrder" class="fulfillment-drawer-panel">
        <div class="drawer-title-row">
          <div>
            <p class="eyebrow">TRUSTED FULFILLMENT</p>
            <h2>{{ selectedFulfillmentOrder.taskTitle || '进行中任务' }}</h2>
            <span>{{ workflowDisplayLabel(selectedFulfillmentOrder) }} · {{ robotTypeLabel(selectedFulfillmentOrder.robotCategory) }}</span>
          </div>
          <button class="ghost-btn" type="button" @click="fulfillmentDrawerVisible = false">关闭</button>
        </div>

        <div class="fulfillment-board drawer-board">
          <section class="fulfillment-log">
            <div class="section-headline">
              <span>DERAS VERIFIED LOG</span>
              <strong>DeRAS履约日志</strong>
            </div>
            <div class="log-list">
              <div
                v-for="log in fulfillmentLogs(selectedFulfillmentOrder)"
                :key="log.key"
                :class="['log-item', log.status]"
              >
                <i></i>
                <div>
                  <span>{{ log.time }}</span>
                  <strong>{{ log.title }}</strong>
                  <p>{{ log.desc }}</p>
                </div>
              </div>
            </div>
          </section>

          <section class="settlement-voucher">
            <div class="section-headline">
              <span>ROBOCOIN VOUCHER</span>
              <strong>RoboCoin结算凭证</strong>
            </div>
            <div class="voucher-grid">
              <span><em>服务时长</em><b>{{ serviceTimeText(selectedFulfillmentOrder) }}</b></span>
              <span><em>平台补助</em><b>{{ formatCoin(selectedFulfillmentOrder.prepaidRobocoin) }} ROC</b></span>
              <span><em>最终应付</em><b>{{ formatCoin(selectedFulfillmentOrder.finalRobocoin) }} ROC</b></span>
              <span><em>平台手续费</em><b>{{ platformFeeText(selectedFulfillmentOrder) }}</b></span>
              <span><em>服务方收入</em><b>{{ providerIncomeText(selectedFulfillmentOrder) }}</b></span>
              <span><em>结算状态</em><b>{{ settlementStatusText(selectedFulfillmentOrder) }}</b></span>
            </div>
            <p>{{ settlementVoucherNote(selectedFulfillmentOrder) }}</p>
          </section>

          <section class="pot-proof-card">
            <div class="section-headline">
              <span>POT OFF-CHAIN PROOF</span>
              <strong>PoT履约证明</strong>
            </div>
            <div class="pot-proof-grid">
              <span>
                <em>证明状态</em>
                <b>{{ potProofStatus(selectedFulfillmentOrder) }}</b>
              </span>
              <span>
                <em>证明数量</em>
                <b>{{ potProofLogs(selectedFulfillmentOrder).length }} 条</b>
              </span>
              <span>
                <em>最近摘要</em>
                <b>{{ shortDigest(latestPotProof(selectedFulfillmentOrder) && latestPotProof(selectedFulfillmentOrder).proofDigest) }}</b>
              </span>
              <span>
                <em>证据类型</em>
                <b>{{ potProofTypes(selectedFulfillmentOrder) }}</b>
              </span>
            </div>
            <p>当前阶段只生成离线可审计证明，不做上链和真实币结算；后续可把摘要锚定到链上。</p>
          </section>
        </div>
      </div>
    </el-drawer>

    <el-dialog
      :model-value="robotDetailVisible"
      @update:model-value="robotDetailVisible = $event"
      width="760px"
      custom-class="robot-detail-dialog"
      append-to-body
    >
      <div v-if="selectedRobotOrder" class="robot-detail-panel">
        <div class="robot-detail-cover">
          <img v-if="selectedRobotOrder.robotCoverImageUrl" :src="selectedRobotOrder.robotCoverImageUrl" :alt="selectedRobotOrder.robotName" />
          <div v-else>ROBOT</div>
        </div>
        <div class="robot-detail-info">
          <p class="eyebrow">ROBOT PROFILE</p>
          <h2>{{ selectedRobotOrder.robotName || '未知机器人' }}</h2>
          <div class="robot-detail-grid">
            <span><em>机器人类型</em><strong>{{ robotTypeLabel(selectedRobotOrder.robotCategory) }}</strong></span>
            <span><em>服务身份</em><strong>{{ roleLabel(selectedRobotOrder) }}</strong></span>
            <span><em>服务者</em><strong>{{ selectedRobotOrder.takerName || '未知服务者' }}</strong></span>
            <span><em>订单阶段</em><strong>{{ workflowLabel(selectedRobotOrder.workflowStatus) }}</strong></span>
          </div>
          <p class="robot-detail-desc">{{ selectedRobotOrder.robotCapabilityDesc || '暂无机器人能力描述。' }}</p>
          <p class="robot-detail-warning">如果现场机器人与这里展示的图片、类型或能力明显不一致，你可以在确认开始或确认结束时拒绝并提交仲裁。</p>
        </div>
      </div>
    </el-dialog>

    <el-dialog
      :model-value="arbitrationVisible"
      @update:model-value="arbitrationVisible = $event"
      width="680px"
      custom-class="arbitration-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <div class="arbitration-panel">
        <p class="eyebrow">DERAS ARBITRATION</p>
        <h2>{{ arbitrationTitle }}</h2>
        <p class="arbitration-desc">拒绝后订单会进入待仲裁状态，进行中的任务列表将不再继续推进。管理员后续会根据理由和凭证进行人工审核。</p>
        <el-form label-position="top">
          <el-form-item label="拒绝理由（必填）">
            <el-input
              v-model.trim="arbitrationForm.reason"
              type="textarea"
              :rows="5"
              maxlength="500"
              show-word-limit
              placeholder="请说明为什么拒绝，例如：现场机器人与封面不一致、机器人类型不对、服务未达到开始条件、清扫结果不合格等。"
            ></el-input>
          </el-form-item>
          <el-form-item label="现场照片（选填）">
            <el-upload
              class="arbitration-upload"
              :action="cloudUploadUrl"
              :show-file-list="false"
              :on-success="handleArbitrationImageSuccess"
              :before-upload="beforeArbitrationImageUpload"
            >
              <div v-if="arbitrationForm.imageUrl" class="arbitration-preview">
                <img :src="arbitrationForm.imageUrl" alt="arbitration proof" />
                <span>重新上传凭证</span>
              </div>
              <div v-else class="arbitration-placeholder">
                <strong>上传现场照片</strong>
                <span>可选，但建议上传机器人或服务结果照片</span>
              </div>
            </el-upload>
          </el-form-item>
        </el-form>
        <div class="arbitration-actions">
          <button class="ghost-btn" type="button" @click="arbitrationVisible = false">取消</button>
          <button class="arbitration-btn" type="button" :disabled="arbitratingId" @click="submitArbitration">
            {{ arbitratingId ? '提交中...' : '拒绝并进入仲裁' }}
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  advanceOrder,
  cancelActiveOrder,
  confirmSubcontractHandover,
  confirmSubcontractPartner,
  createOrderSdkChallenge,
  getActiveOrders,
  getMyOrders,
  getOrderFulfillmentLogs,
  getSdkStageVerification,
  requestOrderArbitration,
  requestSubcontractHandover,
  rejectSubcontractPartner,
  submitOrderAlertReview
} from '../api/orders'
import OrderVehicleMap from '../components/OrderVehicleMap.vue'
import { getAuthUser } from '../utils/auth'
import { CLOUD_UPLOAD_URL, extractCloudUploadUrl, isCloudUploadSuccess } from '../utils/cloud-upload'

export default {
  name: 'ActiveOrders',
  components: {
    OrderVehicleMap
  },
  data() {
    return {
      loading: false,
      advancingId: null,
      cancellingId: null,
      arbitratingId: null,
      sdkVerifyVisible: false,
      robotDetailVisible: false,
      arbitrationVisible: false,
      fulfillmentDrawerVisible: false,
      sdkVerifyingOrder: null,
      selectedRobotOrder: null,
      arbitrationOrder: null,
      selectedFulfillmentOrder: null,
      cloudUploadUrl: CLOUD_UPLOAD_URL,
      arbitrationForm: {
        reason: '',
        imageUrl: '',
        imagePath: ''
      },
      sdkChallenge: null,
      sdkVerifyStatus: 'pending',
      sdkVerifyMessage: '等待工控机SDK上报。',
      sdkAutoAdvancing: false,
      sdkPollTimer: null,
      ordersPollTimer: null,
      clockTimer: null,
      nowTs: Date.now(),
      currentUser: getAuthUser(),
      orders: [],
      fulfillmentLogMap: {},
      reviewingAlertKey: '',
      steps: [
        { value: 'ACCEPTED', label: '机器人已接单', actor: 'system', index: 0 },
        { value: 'DEPARTED', label: '机器人已出发', actor: 'taker', index: 1 },
        { value: 'ARRIVED', label: '机器人已到达', actor: 'taker', index: 2 },
        { value: 'START_REQUESTED', label: '服务者申请开始', actor: 'taker', index: 3 },
        { value: 'STARTED', label: '用户确认开始', actor: 'publisher', index: 4 },
        { value: 'END_REQUESTED', label: '服务者申请结束', actor: 'taker', index: 5 },
        { value: 'FINISHED', label: '用户确认结束', actor: 'publisher', index: 6 }
      ],
      robotTypes: [
        { value: 'CLEANING', label: '清洁机器人' },
        { value: 'DELIVERY', label: '配送机器人' },
        { value: 'SHUTTLE', label: '接驳机器人' },
        { value: 'INSPECTION', label: '巡检机器人' },
        { value: 'GO2', label: '机器狗 Go2' }
      ]
    }
  },
  created() {
    this.fetchOrders()
    this.startClockTimer()
    this.startOrdersPolling()
  },
  beforeUnmount() {
    this.stopSdkPolling()
    this.stopClockTimer()
    this.stopOrdersPolling()
  },
  computed: {
    sdkStageTitle() {
      if (!this.sdkChallenge) return '机器人SDK验证'
      return `验证：${this.workflowLabel(this.sdkChallenge.stage)}`
    },
    sdkVerifyStatusText() {
      const map = {
        creating: '正在建立验证',
        pending: '等待SDK上报',
        passed: 'SDK验证通过',
        failed: '验证未通过',
        expired: '挑战已过期'
      }
      return map[this.sdkVerifyStatus] || '等待SDK上报'
    },
    sdkAgentCommand() {
      const robotId = (this.sdkChallenge && this.sdkChallenge.robotId) || (this.sdkVerifyingOrder && this.sdkVerifyingOrder.robotId)
      if (!robotId) return '请先完成机器人SDK绑定后再启动 Agent'
      return `cd ~/roc-robot-sdk && ./roc-robot-tpm-sdk.sh agent ${robotId}`
    },
    sdkVerifyActionText() {
      if (this.sdkAutoAdvancing) return '正在同步订单状态...'
      if (this.sdkVerifyStatus === 'passed') return `确认${this.workflowLabel(this.sdkChallenge && this.sdkChallenge.stage)}`
      return '等待SDK验证通过'
    },
    arbitrationTitle() {
      if (!this.arbitrationOrder) return '拒绝确认并进入仲裁'
      if (this.arbitrationOrder.workflowStatus === 'START_REQUESTED') return '拒绝开始服务'
      if (this.arbitrationOrder.workflowStatus === 'END_REQUESTED') return '拒绝结束服务'
      return '拒绝确认并进入仲裁'
    }
  },
  methods: {
    apiUrl(path) {
      const base = process.env.VUE_APP_AUTH_BASE_URL || ''
      return `${base}${path}`
    },
    startClockTimer() {
      this.stopClockTimer()
      this.clockTimer = window.setInterval(() => {
        this.nowTs = Date.now()
      }, 1000)
    },
    stopClockTimer() {
      if (this.clockTimer) {
        window.clearInterval(this.clockTimer)
        this.clockTimer = null
      }
    },
    startOrdersPolling() {
      this.stopOrdersPolling()
      this.ordersPollTimer = window.setInterval(() => {
        this.fetchOrders(false)
      }, 15000)
    },
    stopOrdersPolling() {
      if (this.ordersPollTimer) {
        window.clearInterval(this.ordersPollTimer)
        this.ordersPollTimer = null
      }
    },
    async fetchOrders(showLoading = true) {
      if (!this.currentUser || !this.currentUser.id) return
      if (showLoading) this.loading = true
      try {
        const [response, historyResponse] = await Promise.all([
          getActiveOrders(this.currentUser.id),
          getMyOrders({ userId: this.currentUser.id })
        ])
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '进行中任务加载失败')
          return
        }
        const activeOrders = response.data.data || []
        const historyData = historyResponse.data.code === 200 ? historyResponse.data.data || {} : {}
        const acceptedOrders = (historyData.records || []).filter(order => ['ACCEPTED', 'RUNNING'].includes(order.orderStatus))
        this.orders = Array.from(new Map([...acceptedOrders, ...activeOrders].map(order => [order.id, order])).values())
        this.fetchFulfillmentLogsForOrders()
      } catch (error) {
        if (showLoading) this.$message.error('进行中任务请求失败')
      } finally {
        if (showLoading) this.loading = false
      }
    },
    async fetchFulfillmentLogsForOrders() {
      if (!this.currentUser || !this.currentUser.id || !this.orders.length) return
      const pairs = await Promise.all(this.orders.map(async order => {
        try {
          const response = await getOrderFulfillmentLogs(order.id, this.currentUser.id)
          if (response.data.code === 200) {
            return [order.id, response.data.data || []]
          }
        } catch (error) {
          // 旧数据库未执行升级脚本时，页面继续使用前端兜底日志。
        }
        return [order.id, []]
      }))
      const nextMap = {}
      pairs.forEach(([orderId, logs]) => {
        nextMap[orderId] = logs
      })
      this.fulfillmentLogMap = nextMap
    },
    async advance(order) {
      const next = this.nextStep(order)
      if (!next) return
      if (this.requiresSdkVerification(next.value)) {
        await this.openSdkVerification(order, next)
        return
      }
      this.advancingId = order.id
      try {
        const payload = {
          userId: this.currentUser.id,
          targetStatus: next.value
        }
        const response = await advanceOrder(order.id, {
          ...payload
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '流程推进失败')
          return
        }
        this.$message.success(`已确认：${next.label}`)
        this.fetchOrders()
      } catch (error) {
        this.$message.error('流程推进请求失败')
      } finally {
        this.advancingId = null
      }
    },
    async openSdkVerification(order, next) {
      this.advancingId = order.id
      this.sdkVerifyingOrder = order
      this.sdkChallenge = {
        robotId: order.robotId,
        stage: next.value,
        verificationId: null
      }
      this.sdkVerifyStatus = 'creating'
      this.sdkVerifyMessage = this.sdkCreatingMessage(order, next.value)
      this.sdkVerifyVisible = true
      try {
        const response = await createOrderSdkChallenge(order.id, {
          userId: this.currentUser.id,
          targetStatus: next.value
        })
        if (response.data.code !== 200) {
          const message = this.sdkActionErrorMessage(order, response.data.msg || 'SDK验证挑战生成失败')
          this.sdkVerifyStatus = 'failed'
          this.sdkVerifyMessage = message
          this.$message.error(message)
          return
        }
        this.sdkChallenge = response.data.data
        this.sdkVerifyStatus = 'pending'
        this.sdkVerifyMessage = this.sdkWaitingMessage(order)
        this.sdkVerifyVisible = true
        this.startSdkPolling()
      } catch (error) {
        const message = this.sdkActionErrorMessage(order, this.requestErrorMessage(error, 'SDK验证挑战生成失败'))
        this.sdkVerifyStatus = 'failed'
        this.sdkVerifyMessage = message
        this.$message.error(message)
      } finally {
        this.advancingId = null
      }
    },
    startSdkPolling() {
      this.stopSdkPolling()
      this.sdkPollTimer = window.setInterval(() => this.pollSdkVerification(), 2500)
      this.pollSdkVerification()
    },
    stopSdkPolling() {
      if (this.sdkPollTimer) {
        window.clearInterval(this.sdkPollTimer)
        this.sdkPollTimer = null
      }
    },
    async pollSdkVerification() {
      if (!this.sdkChallenge || !this.sdkChallenge.verificationId || !this.currentUser) return
      try {
        const response = await getSdkStageVerification(this.sdkChallenge.verificationId, this.currentUser.id)
        if (response.data.code !== 200) {
          this.sdkVerifyMessage = response.data.msg || 'SDK验证状态读取失败，请检查后端服务。'
          return
        }
        const data = response.data.data || {}
        if (data.status === 'PASSED') {
          this.sdkVerifyStatus = 'passed'
          this.sdkVerifyMessage = data.verificationMessage || 'SDK验证通过，正在同步订单状态。'
          this.stopSdkPolling()
          await this.advanceAfterSdkVerify(true)
          return
        }
        if (data.status === 'FAILED') {
          this.sdkVerifyStatus = 'failed'
          this.sdkVerifyMessage = data.verificationMessage || 'SDK验证未通过，请检查工控机SDK或TPM。'
          this.stopSdkPolling()
          return
        }
        if (data.status === 'EXPIRED') {
          this.sdkVerifyStatus = 'expired'
          this.sdkVerifyMessage = data.verificationMessage || '挑战已过期，请重新点击流程按钮生成新挑战。'
          this.stopSdkPolling()
        }
      } catch (error) {
        this.sdkVerifyMessage = this.requestErrorMessage(error, '正在等待SDK上报，或后端暂时不可达。')
      }
    },
    async advanceAfterSdkVerify(auto = false) {
      if (!this.sdkVerifyingOrder || !this.sdkChallenge) return
      if (this.sdkAutoAdvancing) return
      this.sdkAutoAdvancing = true
      this.advancingId = this.sdkVerifyingOrder.id
      try {
        const response = await advanceOrder(this.sdkVerifyingOrder.id, {
          userId: this.currentUser.id,
          targetStatus: this.sdkChallenge.stage
        })
        if (response.data.code !== 200) {
          const message = response.data.msg || '流程推进失败'
          this.sdkVerifyMessage = message
          this.$message.error(message)
          this.$alert(message, '订单状态同步失败', {
            confirmButtonText: '知道了',
            customClass: 'dark-alert-box'
          })
          return
        }
        const stage = this.sdkChallenge.stage
        const stageLabel = this.workflowLabel(stage)
        const successMessage = stage === 'START_REQUESTED'
          ? '已申请开始服务，等待发布者确认开始计时'
          : `已确认：${stageLabel}`
        this.$message.success(successMessage)
        this.sdkVerifyVisible = false
        await this.fetchOrders()
      } catch (error) {
        const message = auto ? 'SDK验证已通过，但订单状态同步请求失败，请重试。' : '流程推进请求失败'
        this.sdkVerifyMessage = message
        this.$message.error(message)
      } finally {
        this.advancingId = null
        this.sdkAutoAdvancing = false
      }
    },
    sdkCreatingMessage(order, stage) {
      const action = stage === 'END_REQUESTED' ? '结束服务' : '开始服务'
      const offline = this.sdkOfflineWarning(order)
      return offline
        ? `正在建立${action}验证，但机器人 SDK Agent 当前离线：${offline}`
        : `正在建立${action}验证，DeRAS 将生成一次性挑战并等待机器人 SDK 签名。`
    },
    sdkWaitingMessage(order) {
      const offline = this.sdkOfflineWarning(order)
      return offline
        ? `挑战已生成，但机器人 SDK Agent 当前离线：${offline} 请在机器狗终端启动 Agent 后等待自动上报。`
        : '等待工控机上的 SDK Agent 拉取挑战并调用TPM签名。'
    },
    sdkActionErrorMessage(order, message) {
      const offline = this.sdkOfflineWarning(order)
      if (offline) return `机器人 SDK Agent 当前离线，无法申请结束服务。${offline} ${message}`
      return message
    },
    sdkOfflineWarning(order) {
      if (!order) return ''
      const heartbeatAge = this.numberValue(order.sdkHeartbeatAgeSeconds)
      if (order.sdkAgentOnline === false || (Number.isFinite(heartbeatAge) && heartbeatAge > 150)) {
        const heartbeat = Number.isFinite(heartbeatAge)
          ? `最近一次心跳是 ${this.formatAgeText(heartbeatAge)} 前。`
          : '暂未收到 SDK 心跳。'
        const version = order.sdkLastHeartbeatSdkVersion ? `SDK版本 ${order.sdkLastHeartbeatSdkVersion}。` : ''
        return `${heartbeat}${version}`
      }
      return ''
    },
    requestErrorMessage(error, fallback) {
      const responseMessage = error && error.response && error.response.data && error.response.data.msg
      if (responseMessage) return responseMessage
      const code = error && error.code
      const message = error && error.message ? String(error.message) : ''
      if (code === 'ECONNABORTED' || message.toLowerCase().includes('timeout')) return `${fallback}：请求超时，请检查后端服务和局域网连接。`
      if (message.includes('Network Error') || !error || !error.response) return `${fallback}：无法连接后端，请检查 8090 服务是否启动。`
      return fallback
    },
    async cancelOrder(order) {
      if (!this.currentUser || !this.currentUser.id) return
      const stage = this.workflowLabel(order.workflowStatus)
      const fee = this.estimateCancelFee(order)
      const isTakerCancel = Number(order.takerUserId) === Number(this.currentUser.id)
      const cancelTip = isTakerCancel
        ? `当前阶段：${stage}。接单人取消会扣除10信誉分，任务会重新开放给其他服务者。请输入取消原因：`
        : `当前阶段：${stage}。平台将按流程记录取消扣费 ${fee} RoboCoin，剩余平台补助不再继续结算。请输入取消原因：`
      try {
        const reason = await this.$prompt(
          cancelTip,
          '取消任务',
          {
            confirmButtonText: '确认取消',
            cancelButtonText: '再想想',
            inputPlaceholder: isTakerCancel ? '例如：机器人临时不可用 / 无法按时服务' : '例如：计划变更 / 服务不需要了',
            inputValue: isTakerCancel ? '接单人主动取消接单' : '用户主动取消任务',
            inputValidator: value => Boolean(value && value.trim()),
            inputErrorMessage: '请填写取消原因'
          }
        )
        this.cancellingId = order.id
        const response = await cancelActiveOrder(order.id, {
          userId: this.currentUser.id,
          reason: reason.value
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '任务取消失败')
          return
        }
        const cancelled = response.data.data || {}
        this.$message.success(isTakerCancel
          ? '已取消接单，信誉分扣除10分，任务已重新开放'
          : `任务已取消，平台补助扣费 ${this.formatCoin(cancelled.cancelFee)} RoboCoin，剩余 ${this.formatCoin(cancelled.refundRobocoin)} RoboCoin 不再结算`)
        this.fetchOrders()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('任务取消失败')
        }
      } finally {
        this.cancellingId = null
      }
    },
    openRobotDetail(order) {
      this.selectedRobotOrder = order
      this.robotDetailVisible = true
    },
    openFulfillmentDrawer(order) {
      this.selectedFulfillmentOrder = order
      this.fulfillmentDrawerVisible = true
    },
    canRejectForArbitration(order) {
      return Boolean(
        this.currentUser &&
        order &&
        Number(order.publisherUserId) === Number(this.currentUser.id) &&
        ['START_REQUESTED', 'END_REQUESTED'].includes(order.workflowStatus) &&
        ['ACCEPTED', 'RUNNING'].includes(order.orderStatus || 'RUNNING')
      )
    },
    rejectButtonText(order) {
      if (order.workflowStatus === 'START_REQUESTED') return '拒绝开始并仲裁'
      if (order.workflowStatus === 'END_REQUESTED') return '拒绝结束并仲裁'
      return '拒绝并仲裁'
    },
    openArbitration(order) {
      this.arbitrationOrder = order
      this.arbitrationForm = {
        reason: '',
        imageUrl: '',
        imagePath: ''
      }
      this.arbitrationVisible = true
    },
    beforeArbitrationImageUpload(file) {
      const isImage = file.type && file.type.startsWith('image/')
      if (!isImage) {
        this.$message.warning('请上传图片文件')
        return false
      }
      const isLt8M = file.size / 1024 / 1024 < 8
      if (!isLt8M) {
        this.$message.warning('图片大小不能超过 8MB')
        return false
      }
      return true
    },
    handleArbitrationImageSuccess(response) {
      if (!isCloudUploadSuccess(response)) {
        this.$message.error(response && response.msg ? response.msg : '图片上传失败')
        return
      }
      const url = extractCloudUploadUrl(response)
      if (!url) {
        this.$message.error('图片上传失败，接口没有返回可展示的URL')
        return
      }
      this.arbitrationForm.imageUrl = url
      this.arbitrationForm.imagePath = url
      this.$message.success('凭证照片已上传')
    },
    async submitArbitration() {
      if (!this.arbitrationOrder || !this.currentUser) return
      if (!this.arbitrationForm.reason) {
        this.$message.warning('请填写拒绝理由')
        return
      }
      this.arbitratingId = this.arbitrationOrder.id
      try {
        const response = await requestOrderArbitration(this.arbitrationOrder.id, {
          userId: this.currentUser.id,
          stage: this.arbitrationOrder.workflowStatus,
          reason: this.arbitrationForm.reason,
          imageUrl: this.arbitrationForm.imageUrl,
          imagePath: this.arbitrationForm.imagePath
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '仲裁提交失败')
          return
        }
        this.$message.success('订单已进入待仲裁')
        this.arbitrationVisible = false
        this.fetchOrders()
      } catch (error) {
        this.$message.error('仲裁提交请求失败')
      } finally {
        this.arbitratingId = null
      }
    },
    requiresSdkVerification(stage) {
      return ['START_REQUESTED', 'END_REQUESTED'].includes(stage)
    },
    async copyText(text, successMessage = '内容已复制') {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const input = document.createElement('textarea')
          input.value = text
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        this.$message.success(successMessage)
      } catch (error) {
        this.$message.warning('复制失败，请手动复制')
      }
    },
    stepIndex(status) {
      const matched = this.steps.find(item => item.value === (status || 'ACCEPTED'))
      return matched ? matched.index : 0
    },
    nextStep(order) {
      const index = this.stepIndex(order.workflowStatus)
      return this.steps[index + 1] || null
    },
    nextAction(order) {
      const next = this.nextStep(order)
      if (!next || !this.currentUser) return null
      if (next.actor === 'taker' && this.isWorkflowTaker(order, next.value)) {
        return {
          ...next,
          button: next.value === 'DEPARTED'
            ? '确认机器人已出发'
            : (next.value === 'ARRIVED' ? '确认机器人已到达' : (next.value === 'START_REQUESTED' ? '申请开始服务' : '申请结束服务'))
        }
      }
      if (next.actor === 'publisher' && this.isPublisher(order)) {
        return {
          ...next,
          button: next.value === 'STARTED' ? '确认开始服务' : '确认服务结束并结算'
        }
      }
      return null
    },
    isPublisher(order) {
      return Boolean(this.currentUser && order && Number(order.publisherUserId) === Number(this.currentUser.id))
    },
    isTaker(order) {
      return Boolean(this.currentUser && order && Number(order.takerUserId) === Number(this.currentUser.id))
    },
    isSecondaryTaker(order) {
      return Boolean(this.currentUser && order && Number(order.secondaryTakerUserId) === Number(this.currentUser.id))
    },
    isWorkflowTaker(order, targetStatus) {
      if (order && order.executionMode === 'SUBCONTRACT' && ['START_REQUESTED', 'END_REQUESTED'].includes(targetStatus)) {
        return this.isSecondaryTaker(order) && order.subcontractStatus === 'SECONDARY_ACTIVE'
      }
      return this.isTaker(order)
    },
    canConfirmSubcontract(order) {
      return this.isSecondaryTaker(order) && order.subcontractStatus === 'PARTNER_PENDING'
    },
    canRequestHandover(order) {
      return this.isTaker(order) && order.subcontractStatus === 'READY' && order.workflowStatus === 'ARRIVED'
    },
    canConfirmHandover(order) {
      return this.isSecondaryTaker(order) && order.subcontractStatus === 'HANDOVER_PENDING'
    },
    subcontractStatusLabel(value) {
      const map = {
        PARTNER_PENDING: '等待第二段机器人确认',
        READY: '两段方案已锁定',
        HANDOVER_PENDING: '第一段已到达，等待交接核验',
        SECONDARY_ACTIVE: '交接完成，第二段执行中',
        COMPLETED: '两段协作已完成',
        CANCELLED: '分包方案已取消'
      }
      return map[value] || value || '等待方案状态'
    },
    async confirmSubcontract(order) {
      try {
        await this.$confirm('确认后两台机器人、交接点和分账比例不能中途修改。', '确认第二段协作', {
          confirmButtonText: '确认协作',
          cancelButtonText: '稍后处理',
          type: 'warning'
        })
        const response = await confirmSubcontractPartner(order.id, { userId: this.currentUser.id })
        if (response.data.code !== 200) return this.$message.error(response.data.msg || '协作确认失败')
        this.$message.success('两段协作方案已生效')
        this.fetchOrders()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('协作确认失败')
      }
    },
    async rejectSubcontract(order) {
      try {
        const { value } = await this.$prompt('拒绝后整份方案取消，原任务重新开放。请输入原因。', '拒绝协作', {
          confirmButtonText: '确认拒绝',
          cancelButtonText: '返回',
          inputPattern: /^.{2,255}$/,
          inputErrorMessage: '请输入 2 至 255 个字符'
        })
        const response = await rejectSubcontractPartner(order.id, { userId: this.currentUser.id, reason: value })
        if (response.data.code !== 200) return this.$message.error(response.data.msg || '拒绝协作失败')
        this.$message.success('分包方案已取消，任务已重新开放')
        this.fetchOrders()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('拒绝协作失败')
      }
    },
    async requestHandover(order) {
      try {
        const response = await requestSubcontractHandover(order.id, { userId: this.currentUser.id })
        if (response.data.code !== 200) return this.$message.error(response.data.msg || '发起交接失败')
        const nonce = response.data.data && response.data.data.verificationNonce
        if (nonce) await this.copyText(nonce, '交接码已复制，请现场交给第二段机器人')
        this.fetchOrders()
      } catch (error) {
        this.$message.error('发起交接失败')
      }
    },
    async confirmHandover(order) {
      try {
        const { value } = await this.$prompt('请输入第一段机器人现场提供的交接码。', '核验并接管', {
          confirmButtonText: '确认接管',
          cancelButtonText: '返回',
          inputPattern: /^[a-fA-F0-9]{32}$/,
          inputErrorMessage: '交接码应为 32 位字符'
        })
        const response = await confirmSubcontractHandover(order.id, { userId: this.currentUser.id, verificationNonce: value })
        if (response.data.code !== 200) return this.$message.error(response.data.msg || '交接核验失败')
        this.$message.success('交接完成，第二段执行权已生效')
        this.fetchOrders()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('交接核验失败')
      }
    },
    rolePanelClass(order) {
      return {
        publisher: this.isPublisher(order),
        taker: this.isTaker(order) || this.isSecondaryTaker(order)
      }
    },
    rolePanelKicker(order) {
      if (this.isPublisher(order)) return 'PUBLISHER VIEW'
      if (this.isTaker(order)) return 'SERVICE PROVIDER VIEW'
      if (this.isSecondaryTaker(order)) return 'SECONDARY PROVIDER VIEW'
      return 'ORDER VIEW'
    },
    rolePanelTitle(order) {
      const action = this.nextAction(order)
      if (action) return action.button
      if (!this.nextStep(order)) return '任务流程已结束'
      if (this.isSecondaryTaker(order) && order.subcontractStatus === 'PARTNER_PENDING') return '确认是否接受第二段协作'
      if (this.isSecondaryTaker(order) && order.subcontractStatus === 'HANDOVER_PENDING') return '等待现场交接码'
      return this.isPublisher(order) ? '等待服务者推进' : '等待发布者确认'
    },
    rolePanelDesc(order) {
      const status = order.workflowStatus || 'ACCEPTED'
      const isShuttle = order.serviceType === 'SHUTTLE'
      if (this.isPublisher(order)) {
        const shuttleMap = {
          ACCEPTED: '服务者已接单，等待机器人出发前往接驳起点。',
          DEPARTED: '机器人正在前往接驳起点，你可以在地图上查看车辆位置。',
          ARRIVED: '机器人已到达接驳起点，等待服务者申请开始或乘客确认上车。',
          START_REQUESTED: '服务者已申请开始接驳，请确认乘客已上车并开始计时。',
          STARTED: '接驳服务进行中，机器人正在前往接驳终点。',
          END_REQUESTED: '服务者已申请结束接驳，请确认已到达终点并完成结算。',
          FINISHED: '接驳任务已完成。'
        }
        const homeMap = {
          ACCEPTED: '服务者已接单，等待机器人出发。',
          DEPARTED: '机器人正在前往服务地址，你可以在地图上查看车辆位置。',
          ARRIVED: '机器人已到达服务地址，等待服务者申请开始。',
          START_REQUESTED: '服务者已申请开始服务，请确认是否开始计时。',
          STARTED: '服务正在进行中，等待服务者申请结束。',
          END_REQUESTED: '服务者已申请结束，请确认现场结果并完成结算。',
          FINISHED: '任务已完成。'
        }
        return (isShuttle ? shuttleMap : homeMap)[status] || '等待订单状态同步。'
      }
      if (this.isTaker(order)) {
        const shuttleMap = {
          ACCEPTED: '请安排机器人出发，第一段目的地是接驳起点。',
          DEPARTED: '机器人正在前往接驳起点，到达后请确认已到达。',
          ARRIVED: '机器人已到达接驳起点，请在乘客准备好后申请开始服务。',
          START_REQUESTED: '已申请开始接驳，等待发布者确认后开始计时。',
          STARTED: '接驳服务进行中，到达终点后请申请结束。',
          END_REQUESTED: '已申请结束，等待发布者确认结算。',
          FINISHED: '任务已完成。'
        }
        const homeMap = {
          ACCEPTED: '请安排机器人出发前往服务地址。',
          DEPARTED: '机器人正在前往服务地址，到达后请确认已到达。',
          ARRIVED: '机器人已到达服务地址，请申请开始服务。',
          START_REQUESTED: '已申请开始服务，等待发布者确认后开始计时。',
          STARTED: '服务进行中，完成后请申请结束。',
          END_REQUESTED: '已申请结束，等待发布者确认结算。',
          FINISHED: '任务已完成。'
        }
        return (isShuttle ? shuttleMap : homeMap)[status] || '请根据当前阶段推进任务。'
      }
      if (this.isSecondaryTaker(order)) {
        if (order.subcontractStatus === 'PARTNER_PENDING') return '请先核对第二段机器人、交接点和分账比例，再决定是否接受协作。'
        if (order.subcontractStatus === 'READY') return '协作方案已锁定，等待第一段机器人到达交接点。'
        if (order.subcontractStatus === 'HANDOVER_PENDING') return '第一段已发起交接，请现场取得交接码并完成身份核验。'
        if (order.subcontractStatus === 'SECONDARY_ACTIVE') return '执行权已切换到第二段机器人，请按当前流程推进任务。'
      }
      return '查看订单当前阶段和相关服务信息。'
    },
    waitingHint(order) {
      const next = this.nextStep(order)
      if (!next) return ''
      if (next.actor === 'taker') return '等待接单方确认机器人状态。'
      if (next.actor === 'publisher') return next.value === 'STARTED'
        ? '等待任务发布者确认开始，确认后才会正式计时。'
        : '等待任务发布者确认结束，系统会按实际服务时长结算。'
      return '等待 DeRAS 订单中心同步。'
    },
    headline(order) {
      const status = order.workflowStatus || 'ACCEPTED'
      if (this.isPublisher(order)) {
        const map = {
          ACCEPTED: `服务机器人「${order.robotName || '机器人'}」已接单，请等待出发。`,
          DEPARTED: order.serviceType === 'SHUTTLE'
            ? `服务机器人「${order.robotName || '机器人'}」已出发，正在前往接驳起点。`
            : `服务机器人「${order.robotName || '机器人'}」已出发，正在前往服务点。`,
          ARRIVED: order.serviceType === 'SHUTTLE'
            ? `服务机器人「${order.robotName || '机器人'}」已到达接驳起点，等待乘客确认/服务者申请开始。`
            : `服务机器人「${order.robotName || '机器人'}」已到达，等待服务者申请开始。`,
          START_REQUESTED: '服务者已申请开始服务，请确认是否开始计时。',
          STARTED: order.serviceType === 'SHUTTLE'
            ? `服务机器人「${order.robotName || '机器人'}」正在前往接驳终点。`
            : `服务机器人「${order.robotName || '机器人'}」正在执行任务，等待服务者申请结束。`,
          END_REQUESTED: '服务者已申请结束服务，请确认服务结束并完成结算。',
          FINISHED: `服务机器人「${order.robotName || '机器人'}」已完成任务。`
        }
        return map[status] || order.taskMessage || '暂无任务描述'
      }
      if (this.isTaker(order)) {
        const map = {
          ACCEPTED: 'DeRAS 已通过接单，请安排机器人出发。',
          DEPARTED: order.serviceType === 'SHUTTLE' ? '机器人已出发，正在前往接驳起点。' : '机器人已出发，到达服务点后请确认到达。',
          ARRIVED: order.serviceType === 'SHUTTLE' ? '机器人已到达接驳起点，请申请开始接驳。' : '机器人已到达，请申请开始服务。',
          START_REQUESTED: '已申请开始服务，等待用户确认后开始计时。',
          STARTED: order.serviceType === 'SHUTTLE' ? '接驳服务已开始，机器人正在前往终点。' : '任务已开始，完成后请申请结束服务。',
          END_REQUESTED: '已申请结束服务，等待发布者确认结算。',
          FINISHED: '任务已完成，订单流程结束。'
        }
        return map[status] || order.taskMessage || '暂无任务描述'
      }
      return order.taskMessage || '暂无任务描述'
    },
    roleLabel(order) {
      if (!this.currentUser) return '相关方'
      if (this.isPublisher(order)) return '我是发布者'
      if (this.isTaker(order)) return '我是接单人'
      if (this.isSecondaryTaker(order)) return '我是第二段协作方'
      return '相关方'
    },
    canCancel(order) {
      return Boolean(
        this.currentUser &&
        order &&
        ['ACCEPTED', 'RUNNING'].includes(order.orderStatus || 'ACCEPTED') &&
        (Number(order.publisherUserId) === Number(this.currentUser.id) || Number(order.takerUserId) === Number(this.currentUser.id))
      )
    },
    orderAlerts(order) {
      if (!order || ['FINISHED', 'CANCELLED', 'ARBITRATION'].includes(order.orderStatus)) {
        return []
      }
      const alerts = []
      const status = order.workflowStatus || 'ACCEPTED'
      const movingStages = ['DEPARTED', 'STARTED']
      const heartbeatAge = this.numberValue(order.sdkHeartbeatAgeSeconds)
      const locationAge = this.locationAgeSeconds(order)
      const speed = this.numberValue(order.robotSpeed)

      if (order.sdkAgentOnline === false || (Number.isFinite(heartbeatAge) && heartbeatAge > 150)) {
        alerts.push({
          key: 'sdk-offline',
          level: 'danger',
          title: 'SDK Agent 离线',
          desc: Number.isFinite(heartbeatAge)
            ? `最近一次 TPM 心跳是 ${this.formatAgeText(heartbeatAge)} 前，DeRAS 暂时不能确认机器人在线。`
            : '暂未收到 SDK 心跳，出发/开始/结束节点可能无法完成 TPM 验签。',
          actionText: '查看重启 Agent 命令'
        })
      }

      if (!this.hasRobotGps(order)) {
        alerts.push({
          key: 'gps-missing',
          level: 'warning',
          title: '等待车辆 GPS',
          desc: '当前订单还没有拿到车辆真实经纬度，地图会使用模拟坐标兜底展示。',
          actionText: '刷新车辆状态'
        })
      } else if (order.robotLocationOnline === false || (Number.isFinite(locationAge) && locationAge > 180)) {
        alerts.push({
          key: 'gps-stale',
          level: order.robotLocationOnline === false || locationAge > 600 ? 'danger' : 'warning',
          title: 'GPS 超时未更新',
          desc: Number.isFinite(locationAge)
            ? `最近一次车辆 GPS 是 ${this.formatAgeText(locationAge)} 前，位置可能不是实时状态。`
            : 'TDengine 返回的定位状态不是实时数据，请检查车辆定位链路。',
          actionText: '刷新车辆状态'
        })
      }

      if (movingStages.includes(status) && this.hasRobotGps(order) && Number.isFinite(speed) && Math.abs(speed) < 0.5) {
        alerts.push({
          key: 'vehicle-stopped',
          level: status === 'DEPARTED' ? 'warning' : 'danger',
          title: '车辆长时间不动风险',
          desc: status === 'DEPARTED'
            ? '车辆处于出发阶段但速度接近 0，请关注是否堵车、等待或车辆未实际移动。'
            : '服务中车辆速度接近 0，请关注是否停滞、异常等待或需要人工介入。',
          actionText: '联系服务者处理'
        })
      }

      return alerts
    },
    handleAlertAction(order, alert) {
      if (!alert) return
      if (alert.key === 'sdk-offline') {
        const command = order && order.robotId
          ? `cd ~/roc-robot-sdk && ./roc-robot-tpm-sdk.sh agent ${order.robotId}`
          : '请先完成机器人SDK绑定后再启动 Agent'
        this.$alert(command, 'SDK Agent 重启命令', {
          confirmButtonText: '知道了',
          customClass: 'dark-alert-box'
        })
        return
      }
      if (alert.key === 'gps-missing' || alert.key === 'gps-stale') {
        this.fetchOrders(false)
        this.$message.success('已刷新车辆状态')
        if (alert.level === 'danger') {
          this.submitAlertReview(order, alert)
        }
        return
      }
      if (alert.key === 'vehicle-stopped') {
        this.submitAlertReview(order, alert, '车辆长时间不动，需要 DeRAS 复核履约状态。')
      }
    },
    async submitAlertReview(order, alert, defaultReason = '') {
      if (!order || !alert || !this.currentUser) return
      try {
        let reason = defaultReason
        if (!reason) {
          const result = await this.$prompt(
            '请简单说明为什么需要 DeRAS 复核，后续会进入履约日志和消息中心。',
            '提交异常复核',
            {
              confirmButtonText: '提交复核',
              cancelButtonText: '取消',
              inputPlaceholder: '例如：车辆长时间未移动 / GPS不更新 / SDK离线',
              inputValue: alert.desc || alert.title,
              inputValidator: value => Boolean(value && value.trim()),
              inputErrorMessage: '请填写复核说明'
            }
          )
          reason = result.value
        }
        this.reviewingAlertKey = `${order.id}-${alert.key}`
        const response = await submitOrderAlertReview(order.id, {
          userId: this.currentUser.id,
          alertKey: alert.key,
          alertTitle: alert.title,
          alertLevel: alert.level,
          reason
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '提交复核失败')
          return
        }
        this.$message.success('已提交 DeRAS 复核，并写入履约日志')
        await this.fetchFulfillmentLogsForOrders()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('提交复核失败')
        }
      } finally {
        this.reviewingAlertKey = ''
      }
    },
    commandTitle(order) {
      if (!order) return '等待订单同步'
      const action = this.nextAction(order)
      if (action) return action.button
      if (!this.nextStep(order)) return '任务已完成'
      return this.isPublisher(order) ? '等待服务者推进' : '等待发布者确认'
    },
    commandDesc(order) {
      const status = order && (order.workflowStatus || 'ACCEPTED')
      if (this.isPublisher(order)) {
        if (status === 'START_REQUESTED') return '请确认现场条件是否满足，确认后才开始计时和后续 RoboCoin 结算。'
        if (status === 'END_REQUESTED') return '请确认服务结果，确认后 DeRAS 会生成最终结算凭证。'
        return '发布者主要关注车辆位置、可信状态和需要确认的关键节点。'
      }
      if (this.isTaker(order)) {
        return '服务者主要负责推进机器人出发、到达、申请开始和申请结束。需要验签的节点会先经过 SDK/TPM。'
      }
      return 'DeRAS 正在同步当前订单的可执行指令。'
    },
    trustProofTitle(order) {
      if (!order) return '等待验证'
      if (order.sdkAgentOnline === false) return 'SDK Agent 离线'
      if (order.sdkLastHeartbeatVerified === false) return '最近心跳验签失败'
      if (order.sdkAgentOnline) return 'TPM 心跳在线'
      return '等待 SDK 心跳'
    },
    trustProofDesc(order) {
      if (!order) return '暂无可信执行数据。'
      const heartbeat = order.sdkHeartbeatAgeSeconds === null || order.sdkHeartbeatAgeSeconds === undefined
        ? '暂无心跳时间'
        : `${this.formatAgeText(order.sdkHeartbeatAgeSeconds)}前`
      const version = order.sdkLastHeartbeatSdkVersion ? `，SDK版本 ${order.sdkLastHeartbeatSdkVersion}` : ''
      if (order.sdkAgentOnline) return `最近一次 TPM 签名心跳 ${heartbeat}${version}，当前机器人身份可被 DeRAS 追踪。`
      return `最近心跳：${heartbeat}${version}。出发、开始、结束节点仍以 TPM 验签作为可信门槛。`
    },
    shuttleSegmentTitle(order) {
      const status = order && (order.workflowStatus || 'ACCEPTED')
      if (['ACCEPTED', 'DEPARTED'].includes(status)) return '第一段：车辆前往上车点'
      if (['ARRIVED', 'START_REQUESTED'].includes(status)) return '等待上车确认'
      if (['STARTED', 'END_REQUESTED'].includes(status)) return '第二段：车辆前往下车点'
      return '接驳已完成'
    },
    shuttleSegmentDesc(order) {
      if (!order) return '等待接驳路线同步。'
      const pickup = order.beginAddress || '接驳起点'
      const destination = order.endAddress || '接驳终点'
      const status = order.workflowStatus || 'ACCEPTED'
      if (['ACCEPTED', 'DEPARTED'].includes(status)) return `车辆当前目标是上车点：${pickup}。`
      if (['ARRIVED', 'START_REQUESTED'].includes(status)) return `车辆已到达或正在等待从 ${pickup} 开始接驳。`
      if (['STARTED', 'END_REQUESTED'].includes(status)) return `车辆当前目标是下车点：${destination}。`
      return `接驳路线 ${pickup} → ${destination} 已进入完成阶段。`
    },
    fulfillmentLogs(order) {
      if (!order) return []
      const persistedLogs = this.fulfillmentLogMap[order.id] || []
      if (persistedLogs.length) {
        return persistedLogs.map(log => ({
          key: `persisted-${log.id}`,
          status: this.persistedLogStatus(log),
          time: log.createTime || '已记录',
          title: log.eventTitle || '履约记录',
          desc: log.eventContent || log.proofDigest || 'DeRAS 已记录该履约事件。'
        }))
      }
      const currentIndex = this.stepIndex(order.workflowStatus || 'ACCEPTED')
      const logs = this.steps.map(step => ({
        key: step.value,
        status: step.index < currentIndex ? 'done' : (step.index === currentIndex ? 'active' : 'pending'),
        time: this.workflowTimeText(order, step.value),
        title: this.workflowDisplayLabel({ ...order, workflowStatus: step.value }),
        desc: this.workflowLogDesc(order, step.value)
      }))
      logs.push({
        key: 'SDK_HEARTBEAT',
        status: order.sdkAgentOnline ? 'done' : 'pending',
        time: order.sdkLastHeartbeatAt || '等待心跳',
        title: 'SDK/TPM 身份心跳',
        desc: this.trustProofDesc(order)
      })
      if (this.hasRobotGps(order)) {
        logs.push({
          key: 'GPS',
          status: order.robotLocationOnline === false ? 'pending' : 'done',
          time: order.robotLocationTime || '实时定位',
          title: '车辆 GPS 位置',
          desc: `${order.robotLocationDeviceName || order.robotName || '车辆'}：${this.coordinateText(order.robotLng, order.robotLat)}`
        })
      }
      return logs
    },
    potProofLogs(order) {
      const logs = (order && this.fulfillmentLogMap[order.id]) || []
      return logs.filter(log => {
        const proofType = String(log.proofType || '')
        const eventType = String(log.eventType || '')
        return proofType.includes('POT_OFFCHAIN') || eventType === 'POT_PROOF'
      })
    },
    latestPotProof(order) {
      const logs = this.potProofLogs(order)
      return logs.length ? logs[logs.length - 1] : null
    },
    potProofStatus(order) {
      const latest = this.latestPotProof(order)
      if (!latest) return '等待生成'
      if (latest.proofStatus === 'VERIFIED') return '已验证'
      if (latest.proofStatus === 'REVIEWING') return '待复核'
      return latest.proofStatus || '已记录'
    },
    potProofTypes(order) {
      const logs = this.potProofLogs(order)
      const types = [...new Set(logs.map(log => log.proofType).filter(Boolean))]
      return types.length ? types.join(' / ') : '等待证据'
    },
    shortDigest(value) {
      if (!value) return 'pending'
      const text = String(value)
      return text.length > 18 ? `${text.slice(0, 18)}...` : text
    },
    persistedLogStatus(log) {
      if (!log) return 'pending'
      if (['DANGER', 'WARNING'].includes(log.eventLevel)) return 'active'
      if (['REVIEWING', 'PENDING'].includes(log.proofStatus) || ['PENDING', 'PROCESSING'].includes(log.reviewStatus)) return 'active'
      if (['FAILED', 'REJECTED'].includes(log.proofStatus) || log.reviewStatus === 'REJECTED') return 'pending'
      return 'done'
    },
    workflowTimeText(order, stage) {
      if (stage === 'ACCEPTED') return order.createTime || '已创建'
      if (stage === 'START_REQUESTED') return order.serviceStartRequestedTime || '等待申请'
      if (stage === 'STARTED') return order.serviceStartTime || '等待确认'
      if (stage === 'END_REQUESTED') return order.serviceEndRequestedTime || '等待申请'
      if (stage === 'FINISHED') return order.serviceEndTime || '等待结束'
      const reached = this.stepIndex(order.workflowStatus || 'ACCEPTED') >= this.stepIndex(stage)
      return reached ? '已记录' : '未触发'
    },
    workflowLogDesc(order, stage) {
      const shuttle = order.serviceType === 'SHUTTLE'
      const map = {
        ACCEPTED: '订单已进入 DeRAS 履约流程，服务机器人被锁定到该任务。',
        DEPARTED: '出发节点需要 SDK/TPM 验签，通过后证明是绑定机器人推进流程。',
        ARRIVED: shuttle ? '车辆到达接驳起点，等待上车和开始服务确认。' : '车辆到达服务地址，等待开始服务确认。',
        START_REQUESTED: '服务者申请开始，发布者确认后才进入正式计时。',
        STARTED: shuttle ? '发布者确认开始，车辆进入接驳第二段并开始计时。' : '发布者确认开始，服务时长开始计入 RoboCoin 结算。',
        END_REQUESTED: shuttle ? '服务者申请结束，表示车辆已到达接驳终点或等待发布者确认。' : '服务者申请结束，等待发布者确认服务结果。',
        FINISHED: '发布者确认结束，DeRAS 生成最终结算凭证。'
      }
      return map[stage] || '流程日志等待同步。'
    },
    hasRobotGps(order) {
      const lng = this.numberValue(order && (order.robotLng ?? order.vehicleLng ?? order.currentLng))
      const lat = this.numberValue(order && (order.robotLat ?? order.vehicleLat ?? order.currentLat))
      return Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lng) <= 180 && Math.abs(lat) <= 85
    },
    locationAgeSeconds(order) {
      const time = this.parseTime(order && order.robotLocationTime)
      if (!time) return null
      return Math.max(0, Math.floor((this.nowTs - time) / 1000))
    },
    numberValue(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : null
    },
    formatAgeText(seconds) {
      const total = Math.max(0, Number(seconds) || 0)
      if (total < 60) return `${Math.round(total)} 秒`
      const minutes = Math.floor(total / 60)
      if (minutes < 60) return `${minutes} 分钟`
      const hours = Math.floor(minutes / 60)
      const rest = minutes % 60
      return rest ? `${hours} 小时 ${rest} 分钟` : `${hours} 小时`
    },
    workflowLabel(value) {
      const matched = this.steps.find(item => item.value === value)
      return matched ? matched.label : '机器人已接单'
    },
    workflowDisplayLabel(order) {
      if (!order || order.serviceType !== 'SHUTTLE') return this.workflowLabel(order && order.workflowStatus)
      const map = {
        DEPARTED: '前往接驳起点',
        ARRIVED: '已到达接驳起点',
        STARTED: '前往接驳终点'
      }
      return map[order.workflowStatus] || this.workflowLabel(order.workflowStatus)
    },
    serviceTypeLabel(value) {
      const map = {
        HOME_SERVICE: '上门服务',
        SHUTTLE: '接驳任务'
      }
      return map[value] || value || '未知类型'
    },
    serviceTypeDesc(value) {
      if (value === 'SHUTTLE') return '机器人从起点到终点执行接驳'
      if (value === 'HOME_SERVICE') return '机器人到指定地点完成服务'
      return '接口暂未返回任务类型'
    },
    serviceTypeClass(value) {
      if (value === 'SHUTTLE') return 'is-shuttle'
      if (value === 'HOME_SERVICE') return 'is-home'
      return 'is-unknown'
    },
    primaryLocationText(order) {
      if (!order) return '地址未填写'
      if (order.serviceType === 'SHUTTLE') {
        return `起点：${order.beginAddress || '未填写'}`
      }
      return order.serviceAddress || '地址未填写'
    },
    shuttleLocationText(order) {
      if (!order) return '终点：未填写'
      return `终点：${order.endAddress || '未填写'} · ${this.coordinateText(order.endLng, order.endLat)}`
    },
    coordinateText(lng, lat) {
      const parsedLng = Number(lng)
      const parsedLat = Number(lat)
      if (!Number.isFinite(parsedLng) || !Number.isFinite(parsedLat)) {
        return '等待地图坐标'
      }
      return `坐标：${parsedLng.toFixed(6)}, ${parsedLat.toFixed(6)}`
    },
    formatCoin(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(2) : '0.00'
    },
    paymentStatusLabel(value) {
      const map = {
        PAID: '平台补助',
        PARTIAL_REFUNDED: '部分退款',
        DEDUCTED: '已扣费',
        SETTLED: '已结算',
        NEED_EXTRA_PAYMENT: '需补扣',
        REFUNDED: '已退款',
        UNPAID: '未支付'
      }
      return map[value] || value || '平台补助'
    },
    paymentStatusDesc(order) {
      if (order.cancelStage) {
        return `取消阶段：${this.workflowLabel(order.cancelStage)}，扣费 ${this.formatCoin(order.cancelFee)}，退回 ${this.formatCoin(order.refundRobocoin)}。`
      }
      if (order.settlementNote) return order.settlementNote
      return `平台补助：${this.formatCoin(order.prepaidRobocoin)} RoboCoin`
    },
    settlementPreview(order) {
      return '当前阶段由平台提供任务补助；后续接入用户钱包后，将从用户钱包中冻结对应 RoboCoin，任务完成后按实际服务结果结算。'
    },
    serviceTimeText(order) {
      if (this.isTimerRunning(order)) return this.formatLiveDuration(this.serviceElapsedSeconds(order))
      if (order.workflowStatus === 'FINISHED' && order.actualServiceMinutes) return this.formatDuration(order.actualServiceMinutes)
      if (order.serviceStartTime && order.serviceEndTime) return this.formatLiveDuration(this.serviceElapsedSeconds(order))
      if (order.workflowStatus === 'START_REQUESTED') return '等待用户确认开始'
      if (order.workflowStatus === 'END_REQUESTED') return order.serviceStartTime ? this.formatLiveDuration(this.serviceElapsedSeconds(order)) : '等待用户确认结束'
      return '尚未开始计时'
    },
    serviceTimerHint(order) {
      if (this.isTimerRunning(order)) return '发布者已确认开始，计时正在实时走动；服务者申请结束后仍会继续计时，直到发布者确认结束。'
      if (order.workflowStatus === 'FINISHED') return '计时已停止，最终结算按后端记录的开始和结束时间计算。'
      if (order.workflowStatus === 'START_REQUESTED') return '服务者已申请开始，发布者确认后才会正式开始计费。'
      if (order.workflowStatus === 'END_REQUESTED') return '服务者已申请结束，但发布者未确认前计时不会停止。'
      return '服务开始前不会计费，避免提前扣币。'
    },
    settlementText(order) {
      if (order.finalRobocoin && Number(order.finalRobocoin) > 0) {
        return `最终应付 ${this.formatCoin(order.finalRobocoin)}，退回 ${this.formatCoin(order.settlementRefundRobocoin)}，补扣 ${this.formatCoin(order.settlementExtraRobocoin)}。`
      }
      if (order.workflowStatus === 'STARTED') return '服务正在计时，用户确认结束后结算。'
      if (order.workflowStatus === 'START_REQUESTED') return '用户确认开始后才会正式计时。'
      if (order.workflowStatus === 'END_REQUESTED') return '服务者已申请结束，用户确认结束前仍继续计时。'
      return '服务开始后会记录时间。'
    },
    platformFeeText(order) {
      if (order.platformFeeRobocoin !== null && order.platformFeeRobocoin !== undefined) {
        return `${this.formatCoin(order.platformFeeRobocoin)} ROC`
      }
      const finalCoin = Number(order && order.finalRobocoin)
      if (!Number.isFinite(finalCoin) || finalCoin <= 0) return '待结算'
      return `${this.formatCoin(finalCoin * 0.01)} ROC`
    },
    providerIncomeText(order) {
      if (order.providerIncomeRobocoin !== null && order.providerIncomeRobocoin !== undefined) {
        return `${this.formatCoin(order.providerIncomeRobocoin)} ROC`
      }
      const finalCoin = Number(order && order.finalRobocoin)
      if (!Number.isFinite(finalCoin) || finalCoin <= 0) return '待结算'
      return `${this.formatCoin(finalCoin * 0.99)} ROC`
    },
    settlementStatusText(order) {
      if (!order) return '等待订单'
      if (order.workflowStatus === 'FINISHED') return '已生成凭证'
      if (order.workflowStatus === 'END_REQUESTED') return '等待发布者确认'
      if (this.isTimerRunning(order)) return '计时中'
      return '等待开始'
    },
    settlementVoucherNote(order) {
      if (order && order.settlementNote) return order.settlementNote
      if (order && order.workflowStatus === 'FINISHED') return '订单已结束，后端未返回详细结算说明。'
      return '当前阶段先展示平台补助和预估凭证；后续接入链上钱包后，这里会对应冻结、划转和手续费记录。'
    },
    shuttleCurrentTarget(order) {
      const status = order && (order.workflowStatus || 'ACCEPTED')
      if (['ACCEPTED', 'DEPARTED'].includes(status)) return order.beginAddress || '接驳上车点'
      if (['ARRIVED', 'START_REQUESTED'].includes(status)) return '等待乘客上车/确认开始'
      if (['STARTED', 'END_REQUESTED'].includes(status)) return order.endAddress || '接驳下车点'
      return '接驳已完成'
    },
    distanceTextTo(order, type) {
      if (!order) return '等待坐标'
      const robot = this.pointFrom(order.robotLng, order.robotLat)
      const pickup = this.pointFrom(order.beginLng, order.beginLat)
      const destination = this.pointFrom(order.endLng, order.endLat)
      if (type === 'pickup') {
        if (!robot || !pickup) return '等待GPS'
        return this.formatMeters(this.distanceMeters(robot, pickup))
      }
      if (!pickup || !destination) return '等待坐标'
      return this.formatMeters(this.distanceMeters(pickup, destination))
    },
    shuttleEtaText(order) {
      if (!order) return '等待坐标'
      const status = order.workflowStatus || 'ACCEPTED'
      const robot = this.pointFrom(order.robotLng, order.robotLat)
      const pickup = this.pointFrom(order.beginLng, order.beginLat)
      const destination = this.pointFrom(order.endLng, order.endLat)
      let meters = null
      if (['ACCEPTED', 'DEPARTED'].includes(status) && robot && pickup) {
        meters = this.distanceMeters(robot, pickup)
      } else if (['STARTED', 'END_REQUESTED'].includes(status) && robot && destination) {
        meters = this.distanceMeters(robot, destination)
      } else if (pickup && destination) {
        meters = this.distanceMeters(pickup, destination)
      }
      if (!Number.isFinite(meters)) return '等待GPS'
      const minutes = Math.max(1, Math.ceil(meters / (12 * 1000 / 60)))
      return minutes < 60 ? `约 ${minutes} 分钟` : `约 ${Math.floor(minutes / 60)} 小时 ${minutes % 60} 分钟`
    },
    pointFrom(lng, lat) {
      const parsedLng = Number(lng)
      const parsedLat = Number(lat)
      if (!Number.isFinite(parsedLng) || !Number.isFinite(parsedLat)) return null
      return { lng: parsedLng, lat: parsedLat }
    },
    distanceMeters(start, end) {
      if (!start || !end) return null
      const toRad = value => value * Math.PI / 180
      const earthRadius = 6371000
      const dLat = toRad(end.lat - start.lat)
      const dLng = toRad(end.lng - start.lng)
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(start.lat)) * Math.cos(toRad(end.lat)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2)
      return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    },
    formatMeters(meters) {
      if (!Number.isFinite(meters)) return '等待坐标'
      if (meters < 1000) return `${Math.round(meters)} 米`
      return `${(meters / 1000).toFixed(2)} 公里`
    },
    isTimerRunning(order) {
      return Boolean(order && order.serviceStartTime && ['STARTED', 'END_REQUESTED'].includes(order.workflowStatus))
    },
    parseTime(value) {
      if (!value) return null
      if (value instanceof Date) return value.getTime()
      const direct = Date.parse(value)
      if (Number.isFinite(direct)) return direct
      const normalized = String(value).replace(/-/g, '/')
      const timestamp = new Date(normalized).getTime()
      return Number.isFinite(timestamp) ? timestamp : null
    },
    serviceElapsedSeconds(order) {
      const start = this.parseTime(order && order.serviceStartTime)
      if (!start) return 0
      const end = this.parseTime(order && order.serviceEndTime) || this.nowTs
      return Math.max(0, Math.floor((end - start) / 1000))
    },
    formatLiveDuration(seconds) {
      const total = Math.max(0, Number(seconds) || 0)
      const hours = Math.floor(total / 3600)
      const minutes = Math.floor((total % 3600) / 60)
      const restSeconds = total % 60
      const pad = value => String(value).padStart(2, '0')
      return `${pad(hours)}:${pad(minutes)}:${pad(restSeconds)}`
    },
    formatDuration(minutes) {
      const total = Number(minutes)
      if (!Number.isFinite(total) || total <= 0) return '0 分钟'
      const hours = Math.floor(total / 60)
      const rest = Math.round(total % 60)
      if (hours <= 0) return `${rest} 分钟`
      if (rest <= 0) return `${hours} 小时`
      return `${hours} 小时 ${rest} 分钟`
    },
    cancelRate(status) {
      if (status === 'DEPARTED') return 0.2
      if (status === 'ARRIVED') return 0.5
      if (status === 'STARTED') return 0.8
      if (status === 'FINISHED') return 1
      return 0
    },
    estimateCancelFee(order) {
      return this.formatCoin(Number(order.prepaidRobocoin || 0) * this.cancelRate(order.workflowStatus || 'ACCEPTED'))
    },
    cancelRuleText(status) {
      const rate = this.cancelRate(status || 'ACCEPTED')
      if (rate === 0) return '未出发前不扣费'
      return `当前阶段约扣 ${Math.round(rate * 100)}%`
    },
    robotTypeLabel(value) {
      const matched = this.robotTypes.find(item => item.value === value)
      return matched ? matched.label : (value || '未分类')
    }
  }
}
</script>

<style scoped>
.orders-page {
  position: relative;
  min-height: 100vh;
  padding: 34px;
  overflow: hidden;
  color: #fff6ea;
  background:
    radial-gradient(circle at 12% 8%, rgba(255, 183, 82, 0.18), transparent 28%),
    radial-gradient(circle at 88% 10%, rgba(79, 198, 255, 0.17), transparent 24%),
    linear-gradient(135deg, #06111e 0%, #0a1524 48%, #050b15 100%);
}

.orders-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
  background-size: 48px 48px;
  opacity: 0.45;
}

.orders-header,
.orders-grid {
  position: relative;
  z-index: 1;
}

.orders-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-end;
  margin-bottom: 24px;
}

.eyebrow {
  margin: 0 0 10px;
  color: rgba(255, 205, 145, 0.86);
  letter-spacing: 0.35em;
  font-size: 12px;
}

.orders-header h1 {
  margin: 0;
  font-size: clamp(36px, 5vw, 68px);
  font-family: ui-serif, serif;
}

.orders-header p {
  margin: 12px 0 0;
  color: rgba(255, 246, 234, 0.7);
}

.ghost-btn {
  padding: 11px 16px;
  border-radius: 999px;
  color: #fff6ea;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
}

.orders-grid {
  display: grid;
  gap: 20px;
}

.order-card {
  padding: 22px;
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(8, 16, 28, 0.72);
  backdrop-filter: blur(18px);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.22);
}

.order-top {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.order-top h2 {
  margin: 12px 0 8px;
  font-size: 30px;
}

.order-top p,
.hint {
  color: rgba(255, 246, 234, 0.72);
  line-height: 1.7;
}

.role-action-panel {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: center;
  margin: 18px 0;
  padding: 16px;
  border-radius: 20px;
  background:
    linear-gradient(135deg, rgba(142, 215, 255, 0.13), rgba(255, 208, 138, 0.08)),
    rgba(5, 13, 23, 0.72);
  border: 1px solid rgba(142, 215, 255, 0.18);
}

.role-action-panel.taker {
  background:
    linear-gradient(135deg, rgba(143, 241, 199, 0.14), rgba(255, 208, 138, 0.08)),
    rgba(5, 13, 23, 0.72);
  border-color: rgba(143, 241, 199, 0.22);
}

.role-action-panel span,
.taker-operation-panel span {
  display: block;
  margin-bottom: 7px;
  color: rgba(255, 208, 138, 0.86);
  font-size: 12px;
  letter-spacing: 0.14em;
}

.role-action-panel strong,
.taker-operation-panel strong {
  display: block;
  color: #fff6ea;
  font-size: 22px;
}

.role-action-panel p,
.taker-operation-panel p {
  margin: 8px 0 0;
  color: rgba(255, 246, 234, 0.72);
  line-height: 1.7;
}

.subcontract-status-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 0;
  border-top: 1px solid rgba(142, 215, 255, 0.16);
  border-bottom: 1px solid rgba(142, 215, 255, 0.16);
}

.subcontract-status-panel span,
.subcontract-status-panel strong {
  display: block;
}

.subcontract-status-panel span {
  color: #8ed7ff;
  font-size: 12px;
}

.subcontract-status-panel strong {
  margin-top: 6px;
  color: #fff;
  font-size: 18px;
}

.subcontract-status-panel p {
  margin: 7px 0 0;
  color: rgba(238, 244, 255, 0.68);
  line-height: 1.6;
}

.subcontract-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.order-alert-panel {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px;
  margin: -4px 0 18px;
}

.order-alert-item {
  padding: 13px 14px;
  border-radius: 18px;
  background: rgba(255, 208, 138, 0.1);
  border: 1px solid rgba(255, 208, 138, 0.28);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
}

.order-alert-item.danger {
  background: rgba(255, 102, 94, 0.13);
  border-color: rgba(255, 139, 125, 0.42);
}

.order-alert-item span {
  display: block;
  margin-bottom: 7px;
  color: rgba(255, 208, 138, 0.9);
  font-size: 12px;
  font-weight: 900;
}

.order-alert-item.danger span {
  color: #ffb4a8;
}

.order-alert-item strong {
  display: block;
  color: #fff6ea;
  font-size: 17px;
}

.order-alert-item p {
  margin: 7px 0 0;
  color: rgba(255, 246, 234, 0.72);
  line-height: 1.6;
  font-size: 13px;
}

.order-alert-item button {
  margin-top: 10px;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  color: #06111e;
  border: none;
  background: linear-gradient(135deg, #ffd08a, #8ed7ff);
  font-weight: 900;
  cursor: pointer;
}

.order-alert-item.danger button {
  background: linear-gradient(135deg, #ffb4a8, #ffd08a);
}

.deras-monitor {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0 0 18px;
}

.monitor-card {
  padding: 15px;
  border-radius: 18px;
  background:
    linear-gradient(135deg, rgba(142, 215, 255, 0.12), rgba(143, 241, 199, 0.08)),
    rgba(5, 13, 23, 0.72);
  border: 1px solid rgba(142, 215, 255, 0.16);
}

.monitor-card.command {
  border-color: rgba(255, 208, 138, 0.22);
}

.monitor-card.trust {
  border-color: rgba(143, 241, 199, 0.22);
}

.monitor-card.route {
  border-color: rgba(142, 215, 255, 0.24);
}

.monitor-card span,
.section-headline span {
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 208, 138, 0.86);
  font-size: 12px;
  letter-spacing: 0.14em;
}

.monitor-card strong,
.section-headline strong {
  display: block;
  color: #fff6ea;
  font-size: 20px;
}

.monitor-card p {
  margin: 8px 0 0;
  color: rgba(255, 246, 234, 0.7);
  line-height: 1.65;
}

.order-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.sdk-stage-panel {
  color: #fff6ea;
}

.sdk-stage-panel h2 {
  margin: 0 0 10px;
  font-size: 30px;
}

.sdk-stage-desc {
  color: rgba(255, 246, 234, 0.74);
  line-height: 1.8;
}

.sdk-stage-status {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  gap: 8px 12px;
  align-items: center;
  margin: 16px 0;
  padding: 14px;
  border-radius: 18px;
  background: rgba(5, 13, 23, 0.72);
  border: 1px solid rgba(142, 215, 255, 0.16);
}

.sdk-stage-status p {
  grid-column: 2;
  margin: 0;
  color: rgba(255, 246, 234, 0.68);
}

.verify-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ffd08a;
  box-shadow: 0 0 16px rgba(255, 208, 138, 0.5);
}

.verify-dot.passed {
  background: #8ff1c7;
  box-shadow: 0 0 16px rgba(143, 241, 199, 0.5);
}

.verify-dot.failed,
.verify-dot.expired {
  background: #ff8b7d;
  box-shadow: 0 0 16px rgba(255, 139, 125, 0.5);
}

.command-panel {
  display: grid;
  gap: 8px;
  margin-top: 14px;
}

.command-panel span {
  color: rgba(255, 205, 145, 0.86);
  font-size: 12px;
}

.command-panel code {
  display: block;
  overflow-x: auto;
  padding: 14px;
  border-radius: 16px;
  color: #9bd7ff;
  background: rgba(5, 13, 23, 0.84);
  border: 1px solid rgba(142, 215, 255, 0.16);
  white-space: nowrap;
}

.sdk-stage-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 18px;
}

.advance-btn,
.cancel-btn,
.arbitration-btn {
  min-width: 180px;
  height: 46px;
  padding: 0 22px;
  border: none;
  border-radius: 999px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.22);
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.advance-btn.compact {
  min-width: 156px;
  flex: 0 0 auto;
}

.advance-btn {
  color: #06111e;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.cancel-btn {
  color: #ffe9df;
  background: rgba(255, 117, 96, 0.16);
  border: 1px solid rgba(255, 157, 119, 0.36);
}

.arbitration-btn {
  color: #fff6ea;
  background:
    linear-gradient(135deg, rgba(255, 139, 125, 0.26), rgba(255, 208, 138, 0.16)),
    rgba(255, 117, 96, 0.12);
  border: 1px solid rgba(255, 157, 119, 0.42);
}

.advance-btn:hover:not(:disabled),
.cancel-btn:hover:not(:disabled),
.arbitration-btn:hover:not(:disabled) {
  transform: translateY(-2px);
}

.advance-btn:disabled,
.cancel-btn:disabled,
.arbitration-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.identity-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.role-pill,
.status-pill,
.type-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.role-pill {
  color: #06111e;
  background: linear-gradient(135deg, #ffd08a, #8ed7ff);
}

.type-pill {
  min-width: 76px;
  font-weight: 800;
}

.type-pill.is-home {
  color: #d9f7ff;
  background: rgba(79, 198, 255, 0.18);
  border: 1px solid rgba(142, 215, 255, 0.24);
}

.type-pill.is-shuttle {
  color: #f2ffeb;
  background: rgba(143, 241, 199, 0.18);
  border: 1px solid rgba(143, 241, 199, 0.26);
}

.type-pill.is-unknown {
  color: rgba(255, 246, 234, 0.76);
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.status-pill {
  color: #ffe2b8;
  background: rgba(255, 196, 122, 0.14);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 18px 0;
}

.info-grid div,
.robot-strip {
  padding: 14px;
  border-radius: 18px;
  background: rgba(5, 13, 23, 0.72);
  border: 1px solid rgba(142, 215, 255, 0.16);
}

.type-info-card {
  background:
    linear-gradient(135deg, rgba(143, 241, 199, 0.12), rgba(79, 198, 255, 0.1)),
    rgba(5, 13, 23, 0.72);
}

.info-grid span,
.robot-strip span {
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 205, 145, 0.86);
  font-size: 12px;
}

.info-grid p,
.robot-strip p {
  margin: 8px 0 0;
  color: rgba(255, 246, 234, 0.7);
}

.robot-strip {
  display: grid;
  grid-template-columns: 86px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.robot-strip:hover {
  transform: translateY(-2px);
  border-color: rgba(143, 241, 199, 0.32);
  background: rgba(8, 22, 34, 0.82);
}

.robot-detail-btn {
  height: 36px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(143, 241, 199, 0.28);
  color: #8ff1c7;
  background: rgba(143, 241, 199, 0.08);
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;
}

.publisher-tracking {
  margin-bottom: 18px;
}

.taker-operation-panel {
  display: grid;
  gap: 14px;
  margin: 0 0 18px;
  padding: 18px;
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(143, 241, 199, 0.12), rgba(79, 198, 255, 0.1)),
    rgba(5, 13, 23, 0.7);
  border: 1px solid rgba(143, 241, 199, 0.18);
}

.taker-route-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.taker-route-grid span {
  margin: 0;
  padding: 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  letter-spacing: 0;
}

.taker-route-grid em,
.taker-route-grid b {
  display: block;
  font-style: normal;
}

.taker-route-grid em {
  margin-bottom: 8px;
  color: rgba(255, 208, 138, 0.84);
  font-size: 12px;
}

.taker-route-grid b {
  color: rgba(255, 246, 234, 0.88);
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
}

.payment-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.payment-strip div {
  padding: 14px;
  border-radius: 18px;
  background:
    linear-gradient(135deg, rgba(143, 241, 199, 0.1), rgba(255, 208, 138, 0.08)),
    rgba(5, 13, 23, 0.72);
  border: 1px solid rgba(143, 241, 199, 0.16);
}

.payment-strip span {
  display: block;
  margin-bottom: 8px;
  color: rgba(143, 241, 199, 0.9);
  font-size: 12px;
}

.payment-strip strong {
  display: block;
  color: #fff6ea;
}

.payment-strip strong.live-timer {
  font-family: ui-monospace, monospace;
  font-size: 24px;
  letter-spacing: 0;
}

.payment-strip strong.live-timer.running {
  color: #8ff1c7;
  text-shadow: 0 0 18px rgba(143, 241, 199, 0.34);
}

.payment-strip p {
  margin: 8px 0 0;
  color: rgba(255, 246, 234, 0.68);
  line-height: 1.6;
}

.timer-dashboard {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  margin: -2px 0 18px;
  padding: 18px;
  border-radius: 20px;
  border: 1px solid rgba(142, 215, 255, 0.16);
  background:
    linear-gradient(135deg, rgba(142, 215, 255, 0.12), rgba(255, 208, 138, 0.08)),
    rgba(5, 13, 23, 0.7);
}

.timer-dashboard.running {
  border-color: rgba(143, 241, 199, 0.42);
  box-shadow: inset 0 0 0 1px rgba(143, 241, 199, 0.1), 0 16px 38px rgba(0, 0, 0, 0.2);
}

.timer-dashboard.finished {
  border-color: rgba(255, 208, 138, 0.32);
}

.timer-kicker {
  display: block;
  margin-bottom: 8px;
  color: rgba(143, 241, 199, 0.86);
  font-size: 12px;
  letter-spacing: 0.16em;
}

.timer-dashboard strong {
  display: block;
  color: #fff6ea;
  font-family: ui-monospace, monospace;
  font-size: 42px;
  line-height: 1;
  letter-spacing: 0;
}

.timer-dashboard.running strong {
  color: #8ff1c7;
  text-shadow: 0 0 24px rgba(143, 241, 199, 0.38);
}

.timer-dashboard p {
  max-width: 560px;
  margin: 10px 0 0;
  color: rgba(255, 246, 234, 0.7);
  line-height: 1.7;
}

.timer-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(150px, 1fr));
  gap: 10px;
  min-width: 340px;
}

.timer-metrics span {
  padding: 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.timer-metrics em,
.timer-metrics b {
  display: block;
  font-style: normal;
}

.timer-metrics em {
  margin-bottom: 6px;
  color: rgba(255, 208, 138, 0.84);
  font-size: 12px;
}

.timer-metrics b {
  color: rgba(255, 246, 234, 0.86);
  font-size: 13px;
  line-height: 1.45;
  word-break: break-word;
}

.shuttle-eta-panel {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin: 0 0 18px;
  padding: 14px;
  border-radius: 20px;
  background:
    linear-gradient(135deg, rgba(79, 198, 255, 0.12), rgba(143, 241, 199, 0.08)),
    rgba(5, 13, 23, 0.7);
  border: 1px solid rgba(142, 215, 255, 0.16);
}

.shuttle-eta-panel span {
  padding: 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.shuttle-eta-panel em,
.shuttle-eta-panel b {
  display: block;
  font-style: normal;
}

.shuttle-eta-panel em {
  margin-bottom: 7px;
  color: rgba(255, 208, 138, 0.82);
  font-size: 12px;
}

.shuttle-eta-panel b {
  color: #fff6ea;
  line-height: 1.45;
}

.proof-entry-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin: 18px 0;
  padding: 14px 16px;
  border-radius: 18px;
  background:
    linear-gradient(135deg, rgba(143, 241, 199, 0.1), rgba(79, 198, 255, 0.08)),
    rgba(5, 13, 23, 0.68);
  border: 1px solid rgba(142, 215, 255, 0.16);
}

.proof-entry-strip span,
.drawer-title-row span {
  display: block;
  color: rgba(255, 208, 138, 0.82);
  font-size: 12px;
  font-weight: 800;
}

.proof-entry-strip strong {
  display: block;
  margin-top: 4px;
  color: #fff6ea;
  font-size: 18px;
}

.proof-entry-strip p {
  margin: 6px 0 0;
  color: rgba(255, 246, 234, 0.66);
  line-height: 1.55;
}

.compact-proof-btn {
  flex: 0 0 auto;
}

::v-deep .fulfillment-drawer {
  color: #fff6ea;
  background:
    radial-gradient(circle at top right, rgba(143, 241, 199, 0.15), transparent 32%),
    linear-gradient(145deg, #07111d, #0b1826 58%, #10151d);
}

::v-deep .fulfillment-drawer .el-drawer__body {
  padding: 24px;
}

.fulfillment-drawer-panel {
  min-height: 100%;
}

.drawer-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.drawer-title-row h2 {
  margin: 8px 0 8px;
  color: #fff6ea;
  font-size: 28px;
}

.drawer-board {
  margin: 0;
}

.fulfillment-board {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.75fr) minmax(280px, 0.75fr);
  gap: 14px;
  margin: 18px 0;
}

.fulfillment-log,
.settlement-voucher,
.pot-proof-card {
  padding: 16px;
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(143, 241, 199, 0.1), rgba(79, 198, 255, 0.08)),
    rgba(5, 13, 23, 0.7);
  border: 1px solid rgba(142, 215, 255, 0.16);
}

.pot-proof-card {
  background:
    linear-gradient(135deg, rgba(119, 184, 255, 0.12), rgba(255, 208, 138, 0.08)),
    rgba(5, 13, 23, 0.72);
  border-color: rgba(119, 184, 255, 0.18);
}

.section-headline {
  margin-bottom: 14px;
}

.log-list {
  display: grid;
  gap: 10px;
}

.log-item {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  gap: 10px;
  padding: 10px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.log-item i {
  width: 10px;
  height: 10px;
  margin-top: 5px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.28);
  box-shadow: 0 0 0 5px rgba(255, 255, 255, 0.04);
}

.log-item.done i {
  background: #8ff1c7;
  box-shadow: 0 0 0 5px rgba(143, 241, 199, 0.1);
}

.log-item.active {
  border-color: rgba(255, 208, 138, 0.28);
  background: rgba(255, 208, 138, 0.08);
}

.log-item.active i {
  background: #ffd08a;
  box-shadow: 0 0 0 5px rgba(255, 208, 138, 0.12);
}

.log-item span {
  display: block;
  margin-bottom: 5px;
  color: rgba(255, 246, 234, 0.52);
  font-size: 12px;
}

.log-item strong {
  display: block;
  color: #fff6ea;
}

.log-item p {
  margin: 6px 0 0;
  color: rgba(255, 246, 234, 0.68);
  line-height: 1.6;
}

.voucher-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.pot-proof-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.voucher-grid span,
.pot-proof-grid span {
  padding: 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.voucher-grid em,
.voucher-grid b,
.pot-proof-grid em,
.pot-proof-grid b {
  display: block;
  font-style: normal;
}

.voucher-grid em,
.pot-proof-grid em {
  margin-bottom: 7px;
  color: rgba(255, 208, 138, 0.82);
  font-size: 12px;
}

.voucher-grid b,
.pot-proof-grid b {
  color: #fff6ea;
  font-size: 16px;
  word-break: break-word;
}

.settlement-voucher > p,
.pot-proof-card > p {
  margin: 12px 0 0;
  color: rgba(255, 246, 234, 0.7);
  line-height: 1.7;
}

.robot-strip img,
.robot-placeholder {
  width: 86px;
  height: 86px;
  border-radius: 18px;
}

.robot-strip img {
  object-fit: cover;
}

.robot-placeholder {
  display: grid;
  place-items: center;
  color: rgba(255, 246, 234, 0.52);
  background: rgba(255, 255, 255, 0.08);
}

::v-deep .robot-detail-dialog,
::v-deep .arbitration-dialog {
  border-radius: 26px;
  background:
    radial-gradient(circle at top left, rgba(143, 241, 199, 0.18), transparent 34%),
    linear-gradient(145deg, #08131f, #0c1827 54%, #11151d);
  border: 1px solid rgba(142, 215, 255, 0.16);
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.48);
}

::v-deep .robot-detail-dialog .el-dialog__header,
::v-deep .arbitration-dialog .el-dialog__header {
  padding: 0;
}

::v-deep .robot-detail-dialog .el-dialog__body,
::v-deep .arbitration-dialog .el-dialog__body {
  padding: 24px;
}

::v-deep .robot-detail-dialog .el-dialog__headerbtn,
::v-deep .arbitration-dialog .el-dialog__headerbtn {
  z-index: 3;
}

::v-deep .robot-detail-dialog .el-dialog__close,
::v-deep .arbitration-dialog .el-dialog__close {
  color: rgba(255, 246, 234, 0.72);
}

.robot-detail-panel {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 22px;
  color: #fff6ea;
}

.robot-detail-cover {
  min-height: 300px;
  border-radius: 22px;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(142, 215, 255, 0.14), rgba(143, 241, 199, 0.1)),
    rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(142, 215, 255, 0.16);
}

.robot-detail-cover img,
.robot-detail-cover div {
  width: 100%;
  height: 100%;
  min-height: 300px;
}

.robot-detail-cover img {
  object-fit: cover;
}

.robot-detail-cover div {
  display: grid;
  place-items: center;
  color: rgba(255, 246, 234, 0.58);
  font-size: 28px;
  font-weight: 900;
}

.robot-detail-info h2,
.arbitration-panel h2 {
  margin: 8px 0 16px;
  color: #fff6ea;
  font-size: 28px;
}

.robot-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.robot-detail-grid span {
  padding: 12px;
  border-radius: 16px;
  background: rgba(5, 13, 23, 0.68);
  border: 1px solid rgba(142, 215, 255, 0.14);
}

.robot-detail-grid em,
.robot-detail-grid strong {
  display: block;
  font-style: normal;
}

.robot-detail-grid em {
  margin-bottom: 8px;
  color: rgba(255, 208, 138, 0.84);
  font-size: 12px;
}

.robot-detail-grid strong {
  color: #fff6ea;
  line-height: 1.45;
}

.robot-detail-desc,
.robot-detail-warning,
.arbitration-desc {
  margin: 0;
  color: rgba(255, 246, 234, 0.74);
  line-height: 1.75;
}

.robot-detail-warning {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 16px;
  color: #ffe2b8;
  background: rgba(255, 208, 138, 0.1);
  border: 1px solid rgba(255, 208, 138, 0.18);
}

.arbitration-panel {
  color: #fff6ea;
}

.arbitration-panel ::v-deep .el-form-item__label {
  color: rgba(255, 208, 138, 0.9);
  font-weight: 800;
}

.arbitration-panel ::v-deep .el-textarea__inner {
  color: #fff6ea;
  background: rgba(5, 13, 23, 0.76);
  border: 1px solid rgba(142, 215, 255, 0.16);
  border-radius: 16px;
}

.arbitration-panel ::v-deep .el-input__count {
  color: rgba(255, 246, 234, 0.52);
  background: transparent;
}

.arbitration-upload {
  width: 100%;
}

.arbitration-upload ::v-deep .el-upload {
  width: 100%;
}

.arbitration-preview,
.arbitration-placeholder {
  display: grid;
  place-items: center;
  width: 100%;
  min-height: 168px;
  border-radius: 18px;
  border: 1px dashed rgba(143, 241, 199, 0.32);
  background: rgba(5, 13, 23, 0.58);
}

.arbitration-preview {
  position: relative;
  overflow: hidden;
}

.arbitration-preview img {
  width: 100%;
  max-height: 240px;
  object-fit: cover;
}

.arbitration-preview span {
  position: absolute;
  right: 12px;
  bottom: 12px;
  padding: 7px 12px;
  border-radius: 999px;
  color: #06111e;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
  font-weight: 900;
}

.arbitration-placeholder strong,
.arbitration-placeholder span {
  display: block;
}

.arbitration-placeholder strong {
  color: #8ff1c7;
  font-size: 18px;
}

.arbitration-placeholder span {
  margin-top: 8px;
  color: rgba(255, 246, 234, 0.62);
}

.arbitration-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 18px;
}

.timeline {
  display: flex;
  align-items: center;
  gap: 0;
  margin: 16px 0;
  padding: 12px 14px;
  border-radius: 18px;
  background: rgba(5, 13, 23, 0.58);
  border: 1px solid rgba(142, 215, 255, 0.12);
}

.timeline-step {
  position: relative;
  flex: 1;
  min-width: 0;
  color: rgba(255, 246, 234, 0.44);
  text-align: center;
}

.timeline-step:not(:last-child)::after {
  content: "";
  position: absolute;
  top: 13px;
  left: calc(50% + 15px);
  right: calc(-50% + 15px);
  height: 2px;
  background: rgba(255, 255, 255, 0.12);
}

.timeline-step.active {
  color: #fff6ea;
}

.timeline-step.active:not(:last-child)::after {
  background: linear-gradient(90deg, #8ff1c7, rgba(255, 208, 138, 0.72));
}

.timeline-step span {
  position: relative;
  z-index: 1;
  display: inline-flex;
  width: 26px;
  height: 26px;
  margin-bottom: 6px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  font-size: 12px;
}

.timeline-step.active span {
  color: #06111e;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.timeline-step strong {
  display: block;
  overflow: hidden;
  font-size: 12px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

@media (max-width: 860px) {
  .orders-page {
    padding: 18px;
  }

  .orders-header,
  .order-top {
    flex-direction: column;
    align-items: stretch;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .proof-entry-strip,
  .drawer-title-row {
    flex-direction: column;
    align-items: stretch;
  }

  .deras-monitor,
  .fulfillment-board,
  .shuttle-eta-panel {
    grid-template-columns: 1fr;
  }

  .payment-strip {
    grid-template-columns: 1fr;
  }

  .role-action-panel {
    flex-direction: column;
    align-items: stretch;
  }

  .taker-route-grid {
    grid-template-columns: 1fr;
  }

  .timer-dashboard,
  .timer-metrics {
    grid-template-columns: 1fr;
  }

  .timer-dashboard {
    flex-direction: column;
  }

  .timer-metrics {
    min-width: 0;
  }

  .timer-dashboard strong {
    font-size: 34px;
  }

  .timeline {
    overflow-x: auto;
  }

  .timeline-step {
    min-width: 74px;
  }
}
</style>
